import { useCallback, useEffect, useRef, useState } from 'react';

import { cn, ia } from 'lib/helpers/utility';
import { useWindowSize } from 'lib/hooks/useWindowSize';

import Popover from '../Popovers/Popover/Popover';
import { withErrorBoundary } from '../Error/Boundary';
import HorizontalScroll from '../HorizontalScroll/HorizontalScroll';

import ControlItem from './components/ControlItem';

const SegmentedControl = ({
  items = [],
  onClick = () => {},
  active,
  disabled = false,
  count,
  horizontalScroll = false,
  disableOverflow = false,
  loading,
  size = 'default',
  className,
  itemClassName
}) => {
  const [selected, setSelected] = useState(active);
  const containerRef = useRef(null);
  const [visibleItems, setVisibleItems] = useState(items);
  const [hiddenItems, setHiddenItems] = useState([]);
  const { device } = useWindowSize();
  const isMobile = device === 'mobile' || device === 'tablet-sm';
  const isTablet = device === 'tablet' || device === 'tablet-lg';
  const activeItem = active ?? selected;

  const calculateVisibleItems = useCallback(() => {
    if (disableOverflow) {
      setVisibleItems(items);
      setHiddenItems([]);
      return;
    }
    if (!containerRef.current) return;
    const containerWidth = containerRef.current.parentElement.offsetWidth;
    let usedWidth = 0;
    const newVisibleItems = [];
    const newHiddenItems = [];

    items.forEach((item) => {
      const isIconOnly = item.icon && !item.label;

      let itemWidth = 0;

      if (isIconOnly) itemWidth = 50;
      else if (item.label) itemWidth = isMobile ? 150 : isTablet ? 120 : 100;

      if (usedWidth + itemWidth <= containerWidth) {
        newVisibleItems.push(item);
        usedWidth += itemWidth;
      } else {
        if (item.value === activeItem) {
          const lastVisibleItem = newVisibleItems.pop();
          newVisibleItems.push(item);
          if (lastVisibleItem) {
            newHiddenItems.push(lastVisibleItem);
          }
        } else {
          newHiddenItems.push(item);
        }
      }
    });

    setVisibleItems(newVisibleItems);
    setHiddenItems(newHiddenItems);
  }, [items, isMobile, isTablet, activeItem, disableOverflow]);

  useEffect(() => {
    if (!horizontalScroll) {
      calculateVisibleItems();
      window.addEventListener('resize', calculateVisibleItems);
      return () => window.removeEventListener('resize', calculateVisibleItems);
    }
  }, [horizontalScroll, calculateVisibleItems]);

  const handleClick = (item) => {
    if (disabled || item.value === activeItem) return;

    setSelected(item.value);
    onClick(item);
    item.onClick?.(item);

    const isHiddenItem = hiddenItems.some((hiddenItem) => hiddenItem.value === item.value);
    if (isHiddenItem || !horizontalScroll) {
      const isInVisibleItems = visibleItems.some((visibleItem) => visibleItem.value === item.value);
      if (!isInVisibleItems) {
        setVisibleItems((prevVisible) => {
          if (prevVisible.length === 0) return prevVisible;
          return [...prevVisible.slice(0, -1), item];
        });

        setHiddenItems((prevHidden) => {
          const lastVisibleItem = visibleItems[visibleItems.length - 1];
          return [
            ...prevHidden.filter((hiddenItem) => hiddenItem.value !== item.value),
            lastVisibleItem
          ];
        });
      }
    }
  };

  if (!ia(items)) return null;

  const others = items.map((item) => ({
    ...item,
    textClassName: 'text-neutral-900',
    ...(item.actions && {
      component: ({ handleClose }) => (
        <Popover
          options={item.actions}
          placement="bottom-end"
          buttonClassName="border-none"
          icon="context-menu-horizontal"
          position="left"
          onOptionClick={handleClose}
          iconColor="neutral"
          iconsShade={700}
          textClassName="text-neutral-900"
          optionIconColor="neutral"
        />
      )
    }),
    onClick: () => handleClick(item)
  }));

  if (horizontalScroll) {
    return (
      <HorizontalScroll className={cn('rounded-md', disabled && 'cursor-not-allowed', className)}>
        {visibleItems.map((item) => (
          <ControlItem
            key={item.value}
            item={item}
            isActive={active === item.value}
            disabled={disabled}
            onClick={handleClick}
            count={count}
            loading={loading}
            size={size}
            className={itemClassName}
          />
        ))}
      </HorizontalScroll>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn(
        'h-8 w-full max-w-fit grow gap-0.5 bg-[#F3F4F5] p-0.5 xs:h-auto xs:min-h-8',
        'flex items-center rounded-md',
        'shadow-[inset_0px_0px_4px_rgba(55,65,69,0.08)]',
        size === 'large' && 'h-11 gap-1',
        disabled && 'cursor-not-allowed',
        className
      )}>
      {visibleItems.map((item) => (
        <ControlItem
          key={item.value}
          item={item}
          isActive={active === item.value}
          disabled={disabled || item.disabled}
          onClick={handleClick}
          count={count}
          loading={loading}
          size={size}
          className={itemClassName}
        />
      ))}
      {hiddenItems.length > 0 && (
        <Popover
          buttonTheme="neutral"
          className="h-7"
          isFixed
          buttonClassName="rounded-md bg-transparent !min-h-7 !h-7"
          textClassName="text-neutral-800"
          optionGroupLabelClassName="text-neutral-800"
          options={others}
          placement="bottom-end"
          text="More"
          isDropdown
          optionIconSize={16}
          optionIconColor="neutral"
          iconRightColor="neutral-800"
          iconsShade={600}
          icon=""
          menuPortalTarget={document.body}
          panelClassName="overflow-auto max-h-[500px]"
        />
      )}
    </div>
  );
};

/**
 * @type {import('react').ForwardRefExoticComponent<import('./lib/propTypes').SegmentedControlProps & import('react').RefAttributes<HTMLElement>>}
 */

export default withErrorBoundary(SegmentedControl);
